import { supabase } from '../lib/supabase';

/**
 * Fetch all notifications for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of notifications
 */
export const fetchNotifications = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching notifications:', error?.message);
      return [];
    }

    // Normalize field names: map is_read -> read, type -> category
    return (data || [])?.map(normalizeNotification);
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    return [];
  }
};

/**
 * Mark a notification as read
 * @param {string} notificationId - The notification ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsRead = async (notificationId, userId) => {
  try {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error marking notification as read:', error?.message);
      return null;
    }

    return normalizeNotification(data);
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    return null;
  }
};

/**
 * Mark a notification as unread
 * @param {string} notificationId - The notification ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsUnread = async (notificationId, userId) => {
  try {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: false })
      ?.eq('id', notificationId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error marking notification as unread:', error?.message);
      return null;
    }

    return normalizeNotification(data);
  } catch (error) {
    console.error('Failed to mark notification as unread:', error);
    return null;
  }
};

/**
 * Toggle notification read status
 * @param {string} notificationId - The notification ID
 * @param {string} userId - The user ID
 * @param {boolean} currentReadStatus - Current read status
 * @returns {Promise<Object>} Updated notification
 */
export const toggleNotificationReadStatus = async (notificationId, userId, currentReadStatus) => {
  if (currentReadStatus) {
    return markNotificationAsUnread(notificationId, userId);
  } else {
    return markNotificationAsRead(notificationId, userId);
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Updated notifications
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false)
      ?.select();

    if (error) {
      console.error('Error marking all notifications as read:', error?.message);
      return [];
    }

    return (data || [])?.map(normalizeNotification);
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    return [];
  }
};

/**
 * Get notification counts by category
 * @param {Array} notifications - Array of notifications
 * @returns {Object} Counts object
 */
export const calculateNotificationCounts = (notifications) => {
  return {
    all: notifications?.length || 0,
    campaign: notifications?.filter((n) => n?.category === 'campaign')?.length || 0,
    message: notifications?.filter((n) => n?.category === 'message')?.length || 0,
    payment: notifications?.filter((n) => n?.category === 'payment')?.length || 0,
    proposal: notifications?.filter((n) => n?.category === 'proposal')?.length || 0,
    unread: notifications?.filter((n) => !n?.read)?.length || 0,
  };
};

/**
 * Normalize a notification row from the database
 * Maps is_read -> read, type -> category for UI consistency
 * @param {Object} row - Raw database row
 * @returns {Object} Normalized notification
 */
function normalizeNotification(row) {
  if (!row) return null;
  return {
    ...row,
    read: row?.is_read ?? false,
    category: row?.type ?? null,
  };
}
